import React from "react";
import { useTranslation } from "react-i18next";
import { formatLogText } from "../../../utils/formatLogText";
import { isColorLight, getParticipantColor } from "../../../utils/participantColors";
import { useTags } from "../../common/contexts/TagsContext";
import { StoryIcon } from "./StoryIcon";
import { LogIcon } from "./LogIcon";
import { ParticipantBadge } from "../../common/components/ParticipantBadge";
import { useAuth } from "../../auth/contexts/AuthContext";
import DeleteCascadeModal from "./DeleteCascadeModal";
import { socketService } from "../../../services/socket";
import { Story, LogEntry, RundownItem } from "../types";
import { CustomDialog } from "../../common/components/CustomDialog";
import { LogCard } from "../../storyproducer/components/LogCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import config from "../../../config";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/ui/star-rating";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface DataColumnProps {
  stories: Story[];
  rundownItems?: RundownItem[];
  onUpdateStory?: (story: Story) => void;
  sideLogs?: LogEntry[];
  onUpdateSideLogs?: (logs: LogEntry[]) => void;
  onMoveSideLogToStory?: (logId: string, targetStoryId: string | "NEW") => void;
  onCreateStoryFromLogs?: (logIds: string[], storyName: string) => void;
  collapsed?: boolean;
  onToggle?: () => void;
  onToggleWidth?: () => void;
  widthMode?: "normal" | "wide";
  // External Editing
  onEditLog?: (log: LogEntry, source: "active" | "history" | "sweeper", contextId?: string) => void;
  editingLogId?: string | null;
  onEditStory?: (story: Story) => void;
  editingStoryId?: string | null;
  participants?: any[];
  loggerName?: string;
  hideHeader?: boolean;
  hideSweeper?: boolean;
  hideTitle?: boolean;
  activeStoryId?: string;
  onAnalyzeStory?: (storyId: string) => void;
  onAnalyzeRundown?: (rundownId: string) => void;
  onFetchStoryLogs?: (storyId: string, filters?: any) => void;
  onDeleteStory?: (storyId: string) => void;
}

export const DataColumn: React.FC<DataColumnProps> = ({
  stories,
  rundownItems = [],
  onUpdateStory,
  onDeleteStory,
  sideLogs = [],
  onUpdateSideLogs: _onUpdateSideLogs,
  onMoveSideLogToStory,
  onCreateStoryFromLogs,
  collapsed = false,
  onToggle,
  onToggleWidth,
  widthMode = "normal",
  onEditLog,
  editingLogId,
  onEditStory,
  editingStoryId: _editingStoryId,
  participants = [],
  loggerName: _loggerName,
  hideHeader = false,
  hideSweeper = false,
  hideTitle = false,
  activeStoryId,
  onAnalyzeStory: _onAnalyzeStory,
  onAnalyzeRundown,
  onFetchStoryLogs,
}) => {
  const { t } = useTranslation();
  const { getTagLabel, getTagColor, getCameraName, tags, categories, cameras, directors } =
    useTags();

  const { user } = useAuth();
  const isSuperAdmin = user?.role === "Superadmin";
  const [deleteModal, setDeleteModal] = React.useState<{
    isOpen: boolean;
    type: "log" | "story" | "event" | "rundown";
    id: string;
    name: string;
    counts: any;
    isLoading: boolean;
    isDeleting: boolean;
  }>({
    isOpen: false,
    type: "log",
    id: "",
    name: "",
    counts: null,
    isLoading: false,
    isDeleting: false,
  });
  const [globalAlert, setGlobalAlert] = React.useState<{
    isOpen: boolean;
    message: string;
    title?: string;
  } | null>(null);
  const [selectedStories, setSelectedStories] = React.useState<Set<string>>(new Set());
  const [draggedOverStoryId, setDraggedOverStoryId] = React.useState<string | null>(null);
  const [ownerDeleteStory, setOwnerDeleteStory] = React.useState<{
    id: string;
    title: string;
  } | null>(null);
  const [ownerDeleting, setOwnerDeleting] = React.useState(false);

  const handleOwnerDeleteConfirm = React.useCallback(() => {
    if (!ownerDeleteStory || ownerDeleting) return;

    setOwnerDeleting(true);
    const storyId = ownerDeleteStory.id;
    let settled = false;
    const safety = setTimeout(() => {
      if (settled) return;
      settled = true;
      setOwnerDeleting(false);
      setOwnerDeleteStory(null);
      setGlobalAlert({
        isOpen: true,
        title: t("logger.data.deleteStory", "Eliminar historia"),
        message:
          "El servidor no respondió a tiempo. Si la historia sigue apareciendo, recargá la página.",
      });
    }, 8000);

    socketService.emit("story:delete", { id: storyId }, (ack: any) => {
      if (settled) return;
      settled = true;
      clearTimeout(safety);
      setOwnerDeleting(false);
      setOwnerDeleteStory(null);

      if (ack?.ok) {
        onDeleteStory?.(storyId);
        setExpandedStoryIds((prev) => prev.filter((id) => id !== storyId));
        setSelectedStories((prev) => {
          if (!prev.has(storyId)) return prev;
          const next = new Set(prev);
          next.delete(storyId);
          return next;
        });
        return;
      }

      setGlobalAlert({
        isOpen: true,
        title: t("logger.data.deleteStory", "Eliminar historia"),
        message: ack?.error || "Error al eliminar la historia.",
      });
    });
  }, [onDeleteStory, ownerDeleteStory, ownerDeleting, t]);

  // Refs to avoid stale closures
  const selectedStoriesRef = React.useRef(selectedStories);
  React.useEffect(() => {
    selectedStoriesRef.current = selectedStories;
  }, [selectedStories]);
  const deleteModalRef = React.useRef(deleteModal);
  React.useEffect(() => {
    deleteModalRef.current = deleteModal;
  }, [deleteModal]);

  const toggleStorySelection = React.useCallback(
    (id: string, e?: React.MouseEvent) => {
      // Build flat list of story IDs in VISUAL order (rundown by rundown, sorted by createdAt)
      const allStoryIds: string[] = [];
      rundownItems.forEach((rd) => {
        stories
          .filter((s) => s.rundownId === rd.id)
          .sort((a, b) => ((a as any).createdAt || 0) - ((b as any).createdAt || 0))
          .forEach((s) => allStoryIds.push(s.id));
      });
      const clickedIdx = allStoryIds.indexOf(id);

      if (e?.shiftKey && lastClickedStoryRef.current !== null) {
        // Shift+Click: select range from last clicked to current
        const lastIdx = allStoryIds.indexOf(lastClickedStoryRef.current);
        if (lastIdx >= 0 && clickedIdx >= 0) {
          const start = Math.min(lastIdx, clickedIdx);
          const end = Math.max(lastIdx, clickedIdx);
          setSelectedStories((prev) => {
            const n = new Set(prev);
            for (let i = start; i <= end; i++) n.add(allStoryIds[i]);
            return n;
          });
        }
      } else if (e?.metaKey || e?.ctrlKey) {
        // Cmd+Click (Mac) / Ctrl+Click: toggle individual
        setSelectedStories((prev) => {
          const n = new Set(prev);
          if (n.has(id)) n.delete(id);
          else n.add(id);
          return n;
        });
      } else {
        // Normal click: toggle individual (same as Cmd)
        setSelectedStories((prev) => {
          const n = new Set(prev);
          if (n.has(id)) n.delete(id);
          else n.add(id);
          return n;
        });
      }
      lastClickedStoryRef.current = id;
    },
    [rundownItems, stories],
  );
  const lastClickedStoryRef = React.useRef<string | null>(null);

  // Single item delete
  const handleDeleteClick = React.useCallback(
    (type: "log" | "story" | "event" | "rundown", id: string, name: string) => {
      setDeleteModal({
        isOpen: true,
        type,
        id,
        name,
        counts: null,
        isLoading: true,
        isDeleting: false,
      });
      socketService.emit("admin:count-cascade", { type, id });
    },
    [],
  );

  // Bulk delete - two-step: open confirm modal, then execute
  const [bulkConfirmOpen, setBulkConfirmOpen] = React.useState(false);
  const [bulkDeleting, setBulkDeleting] = React.useState(false);

  const _handleDirectBulkDelete = React.useCallback(() => {
    const sz = selectedStoriesRef.current.size;
    console.log("[BULK] handleDirectBulkDelete called, selectedStories:", sz);
    if (sz === 0) return;
    setBulkConfirmOpen(true);
  }, []);

  const _executeBulkDelete = React.useCallback(() => {
    console.log("[BULK] executeBulkDelete CALLED");
    const current = selectedStoriesRef.current;
    console.log("[BULK] selectedStories size:", current.size);
    if (current.size === 0) {
      console.log("[BULK] ABORT: no items");
      return;
    }
    setBulkDeleting(true);
    const items = Array.from(current).map((id) => {
      const story = stories.find((s) => s.id === id);
      return { type: "story" as const, id, name: story?.title || id };
    });
    console.log(
      "[BULK] items to delete:",
      items.length,
      items.map((i) => i.name),
    );
    console.log("[BULK] socket connected:", socketService.connected);
    console.log("[BULK] emitting admin:delete-cascade-bulk...");
    socketService.emit("admin:delete-cascade-bulk", { items });
    console.log("[BULK] emit done, setting up listener + safety reload");
    const h = (d: any) => {
      console.log("[BULK] got result:", d);
      socketService.off("admin:delete-cascade-bulk-result", h);
      clearTimeout(safety);
      window.location.reload();
    };
    socketService.on("admin:delete-cascade-bulk-result", h);
    const safety = setTimeout(() => {
      console.log("[BULK] safety timeout, reloading");
      socketService.off("admin:delete-cascade-bulk-result", h);
      window.location.reload();
    }, 15000);
  }, [stories]);

  // Socket listeners for single delete
  React.useEffect(() => {
    const onCount = (d: any) => {
      if (d.success)
        setDeleteModal((p) => (p.id === d.id ? { ...p, counts: d.counts, isLoading: false } : p));
    };
    const onDel = (d: any) => {
      if (d.success) {
        setDeleteModal((p) => ({ ...p, isOpen: false }));
        if (d.type === "story" && d.id) {
          onDeleteStory?.(d.id);
          setExpandedStoryIds((prev) => prev.filter((id) => id !== d.id));
          setSelectedStories((prev) => {
            if (!prev.has(d.id)) return prev;
            const next = new Set(prev);
            next.delete(d.id);
            return next;
          });
        } else {
          window.location.reload();
        }
      } else setDeleteModal((p) => ({ ...p, isDeleting: false }));
    };
    socketService.on("admin:count-cascade-result", onCount);
    socketService.on("admin:delete-cascade-result", onDel);
    return () => {
      socketService.off("admin:count-cascade-result", onCount);
      socketService.off("admin:delete-cascade-result", onDel);
    };
  }, [onDeleteStory]);

  const handleDeleteConfirm = React.useCallback(() => {
    const modal = deleteModalRef.current;
    setDeleteModal((p) => ({ ...p, isDeleting: true }));
    socketService.emit("admin:delete-cascade", {
      type: modal.type,
      id: modal.id,
      name: modal.name,
    });
  }, []);

  // Helper: Format summary text - sentence case, keep participant names in UPPERCASE,
  // and render [LOG-N] / [TRANS-N] / [OTF-N] references as colored inline badges
  const formatSummary = React.useCallback(
    (text: string): React.ReactNode => {
      if (!text) return "";

      // Extract only the [RESUMEN] block if structured format is detected
      let extractedText = text;
      if (text.includes("[RESUMEN]")) {
        const resumenMatch = text.match(/\[RESUMEN\]\s*([\s\S]*?)\s*\[\/RESUMEN\]/);
        extractedText = resumenMatch ? resumenMatch[1].trim() : text;
      }
      // Remove any remaining structured blocks
      extractedText = extractedText
        .replace(
          /\[(?:META|KEYWORDS|TIPO|STORIES|EVENTOS|HIGHLIGHTS)\][\s\S]*?\[\/(?:META|KEYWORDS|TIPO|STORIES|EVENTOS|HIGHLIGHTS)\]/g,
          "",
        )
        .trim();

      // First, convert to sentence case
      let formatted = extractedText
        .toLowerCase()
        .replace(/^.|\.\s+./g, (c: string) => c.toUpperCase());

      // Then, find and uppercase participant names
      participants.forEach((p: any) => {
        const names = [p.name, p.alias].filter(Boolean);
        names.forEach((name) => {
          if (name && name.length > 2) {
            const regex = new RegExp(`\\b${name.toLowerCase()}\\b`, "gi");
            formatted = formatted.replace(regex, name.toUpperCase());
          }
        });
      });

      // Normalize citation references: strip emoji prefixes like [📋 LOG-N] → [LOG-N]
      formatted = formatted.replace(/\[([^\]]*?)(LOG|TRANS|OTF)-(\d+)\]/gi, "[$2-$3]");
      // Expand grouped citations: [LOG-1, LOG-2] → [LOG-1][LOG-2]
      formatted = formatted.replace(/\[((?:(?:LOG|TRANS|OTF)-\d+,?\s*)+)\]/gi, (match) => {
        const inner = match.slice(1, -1);
        return inner
          .split(/,\s*/)
          .map((p) => `[${p.trim()}]`)
          .join("");
      });

      // Check if there are citation references to render as badges
      if (!/\[(LOG|TRANS|OTF)-\d+\]/i.test(formatted)) {
        return formatted;
      }

      // Split on citation references and render as colored badges
      const parts = formatted.split(/(\[(?:LOG|TRANS|OTF)-\d+\])/gi);
      const result: React.ReactNode[] = [];

      parts.forEach((part, i) => {
        const citMatch = part.match(/^\[(LOG|TRANS|OTF)-(\d+)\]$/i);
        if (citMatch) {
          const prefix = citMatch[1].toUpperCase();
          const num = citMatch[2];
          const isOtf = prefix === "OTF";
          const isLog = prefix === "LOG";
          // Colors matching DataColumn log entry scheme:
          // LOG = pink (#f72d60), TRANS = purple (#9c27b0), OTF = cyan (#00e5ff)
          const bgColor = isOtf ? "#00e5ff" : isLog ? "#f72d60" : "#9c27b0";
          result.push(
            <sup
              key={`cit-${i}`}
              title={`${prefix}-${num}`}
              style={{
                background: bgColor,
                color: "#fff",
                padding: "1px 5px",
                borderRadius: "5px",
                fontSize: "0.6em",
                fontWeight: 700,
                marginLeft: "2px",
                marginRight: "2px",
                display: "inline-block",
                lineHeight: "1.2",
                opacity: 0.9,
                userSelect: "none",
                letterSpacing: "0.3px",
              }}
            >
              {num}
            </sup>,
          );
        } else if (part) {
          result.push(<React.Fragment key={`t-${i}`}>{part}</React.Fragment>);
        }
      });

      return <>{result}</>;
    },
    [participants],
  );

  // Filter states (internal to DataColumn)
  const [searchQuery, setSearchQuery] = React.useState("");
  const [minStars, setMinStars] = React.useState(0); // For story filtering
  const [minLogStars, setMinLogStars] = React.useState(0); // For log filtering
  const [isFiltersExpanded, setIsFiltersExpanded] = React.useState(false);
  const [activeAiSummaryId, setActiveAiSummaryId] = React.useState<string | null>(null);
  const [selectedFilterTags, setSelectedFilterTags] = React.useState<string[]>([]);
  const [selectedFilterParticipants, setSelectedFilterParticipants] = React.useState<string[]>([]);
  const [selectedFilterCameras, setSelectedFilterCameras] = React.useState<string[]>([]);
  const [selectedFilterDirectors, setSelectedFilterDirectors] = React.useState<string[]>([]);
  const [selectedFilterAuthors, setSelectedFilterAuthors] = React.useState<string[]>([]);

  // Compute unique authors from all stories
  const uniqueAuthors = React.useMemo(() => {
    const authors = new Set<string>();
    stories.forEach((s) => {
      if (s.author) authors.add(s.author);
      s.logs?.forEach((l) => {
        if (l.author) authors.add(l.author);
      });
    });
    return Array.from(authors).sort();
  }, [stories]);

  // Filter stories by author if a loggerName is provided
  // DESACTIVADO PARA VER HISTORIAL GLOBAL, NO SÓLO EL MÍO.
  const myStories = stories;

  // Apply global filters to stories

  const filteredStories = myStories;

  // Refetch stories from backend when filters change
  React.useEffect(() => {
    const filters = {
      searchQuery,
      minStars,
      minLogStars,
      tags: selectedFilterTags,
      participants: selectedFilterParticipants,
      cameras: selectedFilterCameras,
      directors: selectedFilterDirectors,
      authors: selectedFilterAuthors,
    };

    const timer = setTimeout(() => {
      socketService.emit("get-filtered-project-stories", null, filters);
    }, 400);
    return () => clearTimeout(timer);
  }, [
    searchQuery,
    minStars,
    minLogStars,
    selectedFilterTags,
    selectedFilterParticipants,
    selectedFilterCameras,
    selectedFilterDirectors,
    selectedFilterAuthors,
  ]);

  // State to track expanded stories and rundowns
  const [expandedStoryIds, setExpandedStoryIds] = React.useState<string[]>([]);
  const [expandedRundownIds, setExpandedRundownIds] = React.useState<string[]>([]);

  // Auto-expand the active story and its parent rundown
  React.useEffect(() => {
    if (!activeStoryId) return;

    // Ensure story is expanded
    setExpandedStoryIds((prev) => (prev.includes(activeStoryId) ? prev : [...prev, activeStoryId]));

    // Find the story to get its rundownId and expand it too
    const activeS = stories.find((s) => s.id === activeStoryId);
    if (activeS && activeS.rundownId) {
      const rId = activeS.rundownId;
      setExpandedRundownIds((prev) => (prev.includes(rId) ? prev : [...prev, rId]));
    }
  }, [activeStoryId, stories]);

  const toggleRundownExpansion = (id: string) => {
    setExpandedRundownIds((prev) =>
      prev.includes(id) ? prev.filter((rid) => rid !== id) : [...prev, id],
    );
  };

  // Auto-fetch logs for expanded stories if they are missing (e.g. after a filter update or init)
  React.useEffect(() => {
    const filters = {
      searchQuery,
      minStars,
      minLogStars,
      tags: selectedFilterTags,
      participants: selectedFilterParticipants,
      cameras: selectedFilterCameras,
      directors: selectedFilterDirectors,
      authors: selectedFilterAuthors,
    };

    expandedStoryIds.forEach((id) => {
      const s = myStories.find((st) => st.id === id);
      if (s && !s.logs) {
        // Only fetch if they are actually missing
        if (onFetchStoryLogs) {
          onFetchStoryLogs(id, filters);
        }
      }
    });
  }, [
    expandedStoryIds,
    myStories,
    onFetchStoryLogs,
    searchQuery,
    minStars,
    minLogStars,
    selectedFilterTags,
    selectedFilterParticipants,
    selectedFilterCameras,
    selectedFilterDirectors,
    selectedFilterAuthors,
  ]);

  const toggleExpansion = (id: string) => {
    const isExpanded = expandedStoryIds.includes(id);

    if (isExpanded) {
      setExpandedStoryIds((prev) => prev.filter((sid) => sid !== id));
    } else {
      setExpandedStoryIds((prev) => [...prev, id]);
      // Fetch logs if needed
      const s = stories.find((s) => s.id === id);
      if (s && (!s.logs || s.logs.length === 0)) {
        if (onFetchStoryLogs) {
          onFetchStoryLogs(id, {
            searchQuery,
            minStars,
            minLogStars,
            tags: selectedFilterTags,
            participants: selectedFilterParticipants,
            cameras: selectedFilterCameras,
            directors: selectedFilterDirectors,
            authors: selectedFilterAuthors,
          });
        }
      }
    }
  };

  const [_editingStoryValue, _setEditingStoryValue] = React.useState<string>("");
  const [hoveredStoryId, setHoveredStoryId] = React.useState<string | null>(null);
  const [hoveredRundownId, setHoveredRundownId] = React.useState<string | null>(null);
  const hoverTimeoutRef = React.useRef<any>(null);

  const _handleStoryHoverEnter = (id: string) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredRundownId(null);
    setHoveredStoryId(id);
  };

  const _handleStoryHoverLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredStoryId(null);
    }, 300);
  };

  const handleRundownHoverEnter = (id: string) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredStoryId(null);
    setHoveredRundownId(id);

    if (onAnalyzeRundown) {
      const rundown = rundownItems.find((r) => r.id === id);
      // Check if summary is missing (and maybe add a simplified check to avoid repeatedly requesting if it's pending?
      // For now, rely on backend or socket debounce, or UI "Analyzando..." state implicit)
      if (rundown && !(rundown as any).summary) {
        onAnalyzeRundown(id);
      }
    }
  };

  const handleRundownHoverLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredRundownId(null);
    }, 300);
  };

  // Auto-expand stories AND rundowns when filters change
  React.useEffect(() => {
    const hasActiveFilters =
      minStars > 0 ||
      minLogStars > 0 ||
      selectedFilterTags.length > 0 ||
      selectedFilterParticipants.length > 0 ||
      selectedFilterCameras.length > 0 ||
      selectedFilterDirectors.length > 0 ||
      selectedFilterAuthors.length > 0 ||
      searchQuery.trim() !== "";
    const hasLogLevelFilters =
      minLogStars > 0 ||
      selectedFilterTags.length > 0 ||
      selectedFilterParticipants.length > 0 ||
      selectedFilterCameras.length > 0 ||
      selectedFilterAuthors.length > 0 ||
      searchQuery.trim() !== "";

    if (hasLogLevelFilters && filteredStories.length > 0) {
      // Auto-expand ALL filtered stories AND their parent rundowns (only for log-level filters)
      const storiesToExpand = filteredStories.map((story) => story.id);

      // Get unique rundown IDs from filtered stories
      const rundownIds = [
        ...new Set(
          filteredStories.map((story) => story.rundownId).filter((id): id is string => Boolean(id)),
        ),
      ];

      // Combine story IDs and rundown IDs
      const allToExpand = [...storiesToExpand, ...rundownIds];

      setExpandedStoryIds(allToExpand);
    } else if (!hasActiveFilters) {
      // No filters: collapse all
      setExpandedStoryIds([]);
    } else if (minStars > 0 && !hasLogLevelFilters) {
      // Only story stars filter: keep collapsed but expand rundowns
      const rundownIds = [
        ...new Set(
          filteredStories.map((story) => story.rundownId).filter((id): id is string => Boolean(id)),
        ),
      ];
      setExpandedStoryIds(rundownIds);
    }
  }, [
    minStars,
    minLogStars,
    selectedFilterTags,
    selectedFilterParticipants,
    selectedFilterCameras,
    selectedFilterAuthors,
    searchQuery,
    filteredStories.map((s) => s.id).join(","),
  ]);

  // Selection State
  const [selectedLogIds, setSelectedLogIds] = React.useState<string[]>([]);
  const [newStoryName, setNewStoryName] = React.useState("");
  const [showNewStoryInput, setShowNewStoryInput] = React.useState(false);

  const handleSelectLog = (logId: string) => {
    setSelectedLogIds((prev) =>
      prev.includes(logId) ? prev.filter((id) => id !== logId) : [...prev, logId],
    );
  };

  const _handleMoveToStory = (targetStoryId: string) => {
    if (selectedLogIds.length > 0 && onMoveSideLogToStory) {
      selectedLogIds.forEach((logId) => onMoveSideLogToStory(logId, targetStoryId));
      setSelectedLogIds([]);
    }
  };

  const handleCreateNewStory = () => {
    if (selectedLogIds.length > 0 && onCreateStoryFromLogs) {
      const storyName = newStoryName.trim() || `Story (${selectedLogIds.length} logs)`;
      onCreateStoryFromLogs(selectedLogIds, storyName);
      setSelectedLogIds([]);
      setNewStoryName("");
      setShowNewStoryInput(false);
    }
  };

  if (collapsed) {
    return (
      <button
        data-testid="data-column-expand"
        onClick={onToggle}
        style={{
          width: "44px",
          height: "100%",
          background: "var(--color-panel-bg)",
          border: "1px solid var(--color-panel-border)",
          borderRadius: "12px",
          color: "var(--color-accent-danger)",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          transition: "all 0.2s",
          fontSize: "var(--text-xs)",
          fontWeight: 800,
          letterSpacing: "1px",
          writingMode: "vertical-rl",
          textOrientation: "mixed",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(247, 45, 96, 0.08)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "var(--color-panel-bg)")}
        title={t("logger.data.expandHistory")}
      >
        <i
          className="bx bx-chevron-left"
          style={{ fontSize: "var(--text-lg)", writingMode: "horizontal-tb" }}
        ></i>
        {t("logger.data.history")}
      </button>
    );
  }

  return (
    <div
      data-testid="data-column"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "var(--color-panel-bg)",
        position: "relative",
        overflow: "hidden",
        borderRadius: "var(--border-radius-md)",
        boxShadow: "var(--shadow-card)",
        border: "1px solid var(--color-panel-border)",
      }}
    >
      {/* Header */}
      {!hideHeader && (
        <div
          data-testid="data-column-header"
          style={{
            borderBottom: "1px solid var(--color-panel-border)",
            background: "var(--color-panel-bg)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Module Title - Full Width Header like Rundown */}
          {!hideTitle && (
            <div
              style={{
                padding: "12px 20px",
                borderBottom: "1px solid var(--color-panel-border)",
                background: "var(--color-bg-base)",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <i
                  className="bx bx-history"
                  style={{ fontSize: "var(--text-lg)", color: "var(--color-accent-danger)" }}
                ></i>
                <span
                  style={{
                    fontWeight: "900",
                    letterSpacing: "2px",
                    fontSize: "var(--text-md)",
                    color: "#ffffff",
                  }}
                >
                  {t("logger.data.history")}
                </span>
              </div>
              {onToggle && (
                <div style={{ display: "flex", gap: "5px" }}>
                  {onToggleWidth && (
                    <button
                      data-testid="data-column-toggle-width"
                      onClick={onToggleWidth}
                      style={{
                        background:
                          widthMode === "wide" ? "rgba(247, 45, 96, 0.2)" : "rgba(0,0,0,0.3)",
                        border:
                          widthMode === "wide"
                            ? "1px solid var(--color-accent-danger)"
                            : "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "6px",
                        color: widthMode === "wide" ? "#fff" : "#888",
                        cursor: "pointer",
                        padding: "4px 6px",
                        fontSize: "var(--text-base)",
                        display: "flex",
                        alignItems: "center",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "var(--color-accent-danger)";
                        e.currentTarget.style.borderColor = "var(--color-accent-danger)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = widthMode === "wide" ? "#fff" : "#888";
                        e.currentTarget.style.borderColor =
                          widthMode === "wide"
                            ? "var(--color-accent-danger)"
                            : "rgba(255,255,255,0.1)";
                      }}
                      title={t("logger.data.changeWidth")}
                    >
                      <i
                        className={
                          widthMode === "wide"
                            ? "bx bx-collapse-horizontal"
                            : "bx bx-expand-horizontal"
                        }
                      ></i>
                    </button>
                  )}
                  <button
                    data-testid="data-column-toggle-collapse"
                    onClick={onToggle}
                    style={{
                      background: "rgba(0,0,0,0.3)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "6px",
                      color: "#888",
                      cursor: "pointer",
                      padding: "4px 6px",
                      fontSize: "var(--text-base)",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "var(--color-accent-danger)";
                      e.currentTarget.style.borderColor = "var(--color-accent-danger)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "#888";
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                    }}
                    title={t("logger.data.collapseHistory")}
                  >
                    <i className="bx bx-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Content Wrapper */}
          <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
            <div
              data-testid="data-column-filters-toggle"
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <span
                style={{
                  fontWeight: "900",
                  letterSpacing: "2px",
                  fontSize: "var(--text-sm)",
                  color: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    fontSize: "var(--text-2xs)",
                    transition: "transform 0.2s",
                    transform: isFiltersExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                  }}
                >
                  ▼
                </span>
                {t("logger.data.filters")}
              </span>

              {isFiltersExpanded && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  {/* Story Star Filter (Pink) */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "var(--text-2xs)",
                        color: "#666",
                        fontWeight: "bold",
                        letterSpacing: "1px",
                      }}
                    >
                      {t("logger.data.story")}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        background: "rgba(0,0,0,0.4)",
                        padding: "4px 10px",
                        borderRadius: "10px",
                        border: "1px solid rgba(255,255,255,0.1)",
                        alignItems: "center",
                      }}
                    >
                      <StarRating
                        value={minStars}
                        onChange={(v) => setMinStars(v)}
                        activeColor="var(--color-accent-success)"
                        inactiveColor="#222"
                        size="var(--text-base)"
                        gap="4px"
                        glow
                        glowColor="rgba(0, 208, 132, 0.3)"
                        bold
                      />
                    </div>
                  </div>

                  {/* Log Star Filter (Orange) */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "var(--text-2xs)",
                        color: "#666",
                        fontWeight: "bold",
                        letterSpacing: "1px",
                      }}
                    >
                      {t("logger.data.log")}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        background: "rgba(0,0,0,0.4)",
                        padding: "4px 10px",
                        borderRadius: "10px",
                        border: "1px solid rgba(255,255,255,0.1)",
                        alignItems: "center",
                      }}
                    >
                      <StarRating
                        value={minLogStars}
                        onChange={(v) => setMinLogStars(v)}
                        inactiveColor="#222"
                        size="var(--text-base)"
                        gap="4px"
                        glow
                        glowColor="rgba(255,170,0,0.3)"
                        bold
                      />
                    </div>
                  </div>
                  <span
                    onClick={() => {
                      setSearchQuery("");
                      setMinStars(0);
                      setMinLogStars(0);
                      setSelectedFilterTags([]);
                      setSelectedFilterParticipants([]);
                      setSelectedFilterCameras([]);
                    }}
                    style={{
                      fontSize: "var(--text-xs)",
                      color: "#666",
                      cursor: "pointer",
                      textTransform: "uppercase",
                      fontWeight: "bold",
                    }}
                  >
                    {t("logger.data.clear")}
                  </span>
                </div>
              )}
            </div>

            {/* Collapsible Content */}
            {isFiltersExpanded && (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {/* Search Bar */}
                <div style={{ position: "relative" }}>
                  <i
                    className="bx bx-search"
                    style={{
                      position: "absolute",
                      left: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#666",
                      zIndex: 1,
                    }}
                  ></i>
                  <Input
                    data-testid="data-column-search"
                    type="text"
                    placeholder={t("logger.data.searchPlaceholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 pl-8 bg-black/20 text-xs font-bold"
                  />
                </div>

                {/* Tags & Cameras Filter (Directly visible, grouped by category) */}
                {(() => {
                  // Group tags by category and sort internally
                  const tagsByCategory = tags.reduce((acc: any, t: any) => {
                    if (!t.categoryId) return acc;
                    if (!acc[t.categoryId]) acc[t.categoryId] = [];
                    acc[t.categoryId].push(t);
                    return acc;
                  }, {});

                  // Sort tags within categories
                  Object.keys(tagsByCategory).forEach((catId) => {
                    tagsByCategory[catId].sort((a: any, b: any) =>
                      (a.label || a.name || "").localeCompare(b.label || b.name || ""),
                    );
                  });

                  // Sort cameras
                  const sortedCameras = [...cameras].sort((a, b) => a.name.localeCompare(b.name));
                  // Sort directors
                  const sortedDirectors = [...directors].sort((a, b) =>
                    a.name.localeCompare(b.name),
                  );

                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        padding: "8px",
                        background: "rgba(0,0,0,0.2)",
                        borderRadius: "6px",
                        maxHeight: "250px",
                        overflowY: "auto",
                      }}
                    >
                      {/* Logger / Author Filter (MOVED TO TOP) */}
                      {uniqueAuthors.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                            marginBottom: "8px",
                            borderBottom: "1px solid rgba(255,255,255,0.05)",
                            paddingBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "var(--text-2xs)",
                              color: "var(--color-accent-primary)",
                              fontWeight: "bold",
                              letterSpacing: "1px",
                            }}
                          >
                            {t("logger.data.loggers")}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "6px",
                              overflowX: "auto",
                              paddingBottom: "4px",
                              scrollbarWidth: "none",
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              onClick={() => setSelectedFilterAuthors([])}
                              style={{
                                fontSize: "var(--text-xs)",
                                background:
                                  selectedFilterAuthors.length === 0
                                    ? "var(--color-accent-primary)"
                                    : "rgba(255,255,255,0.05)",
                                color:
                                  selectedFilterAuthors.length === 0
                                    ? "#fff"
                                    : "var(--color-text-primary)",
                                padding: "4px 10px",
                                borderRadius: "12px",
                                cursor: "pointer",
                                fontWeight: selectedFilterAuthors.length === 0 ? "bold" : "normal",
                                border:
                                  selectedFilterAuthors.length === 0
                                    ? "1px solid var(--color-accent-primary)"
                                    : "1px solid rgba(255,255,255,0.1)",
                                textTransform: "uppercase",
                              }}
                            >
                              {t("logger.data.all")}
                            </span>
                            {uniqueAuthors.map((author) => {
                              const isSelected = selectedFilterAuthors.includes(author);
                              return (
                                <span
                                  key={author}
                                  onClick={() =>
                                    setSelectedFilterAuthors((prev) =>
                                      isSelected
                                        ? prev.filter((a) => a !== author)
                                        : [...prev, author],
                                    )
                                  }
                                  style={{
                                    fontSize: "var(--text-xs)",
                                    background: isSelected
                                      ? "var(--color-accent-primary)"
                                      : "rgba(255,255,255,0.05)",
                                    color: isSelected ? "#fff" : "var(--color-text-primary)",
                                    padding: "4px 10px",
                                    borderRadius: "12px",
                                    cursor: "pointer",
                                    fontWeight: isSelected ? "bold" : "normal",
                                    border: isSelected
                                      ? "1px solid var(--color-accent-primary)"
                                      : "1px solid rgba(255,255,255,0.1)",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {author}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Cameras Row */}
                      {sortedCameras.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "5px",
                            paddingBottom: "6px",
                            borderBottom: "1px solid rgba(255,255,255,0.05)",
                            marginBottom: "2px",
                          }}
                        >
                          {sortedCameras.map((cam: any) => {
                            const isSelected = selectedFilterCameras.includes(cam.id);
                            const color = "#ffaa00"; // Standard Camera color
                            const textColor = isSelected
                              ? isColorLight(color)
                                ? "#000"
                                : "#fff"
                              : "#fff";
                            return (
                              <span
                                key={cam.id}
                                onClick={() =>
                                  setSelectedFilterCameras((prev) =>
                                    isSelected
                                      ? prev.filter((id) => id !== cam.id)
                                      : [...prev, cam.id],
                                  )
                                }
                                style={{
                                  fontSize: "var(--text-2xs)",
                                  background: isSelected ? color : "transparent",
                                  color: textColor,
                                  padding: "3px 8px",
                                  borderRadius: "6px",
                                  fontWeight: 900,
                                  cursor: "pointer",
                                  border: `1px solid ${color}`,
                                  boxShadow: isSelected ? `0 0 12px ${color}40` : "none",
                                  transition: "all 0.1s ease",
                                  textTransform: "uppercase",
                                  opacity: isSelected ? 1 : 0.8,
                                }}
                              >
                                {getCameraName(cam.id)}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Directors Row */}
                      {sortedDirectors.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "5px",
                            paddingBottom: "6px",
                            borderBottom: "1px solid rgba(255,255,255,0.05)",
                            marginBottom: "2px",
                          }}
                        >
                          {sortedDirectors.map((dir: any) => {
                            const isSelected = selectedFilterDirectors.includes(dir.id);
                            const color = "#9c27b0"; // Standard Director color
                            const textColor = isSelected
                              ? isColorLight(color)
                                ? "#000"
                                : "#fff"
                              : "#fff";
                            return (
                              <span
                                key={dir.id}
                                onClick={() =>
                                  setSelectedFilterDirectors((prev) =>
                                    isSelected
                                      ? prev.filter((id) => id !== dir.id)
                                      : [...prev, dir.id],
                                  )
                                }
                                style={{
                                  fontSize: "var(--text-2xs)",
                                  background: isSelected ? color : "transparent",
                                  color: textColor,
                                  padding: "3px 8px",
                                  borderRadius: "6px",
                                  fontWeight: 900,
                                  cursor: "pointer",
                                  border: `1px solid ${color}`,
                                  boxShadow: isSelected ? `0 0 12px ${color}40` : "none",
                                  transition: "all 0.1s ease",
                                  textTransform: "uppercase",
                                  opacity: isSelected ? 1 : 0.8,
                                }}
                              >
                                {dir.name}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Category Groups */}
                      {categories
                        .filter(
                          (c: any) => c.id !== "TECHNICAL" && tagsByCategory[c.id]?.length > 0,
                        )
                        .map((cat: any) => (
                          <div
                            key={cat.id}
                            style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}
                          >
                            {tagsByCategory[cat.id].map((t: any) => {
                              const isSelected = selectedFilterTags.includes(t.id);
                              const color = cat.color || "#888";
                              const textColor = isSelected
                                ? isColorLight(color)
                                  ? "#000"
                                  : "#fff"
                                : "#fff";
                              return (
                                <span
                                  key={t.id}
                                  onClick={() =>
                                    setSelectedFilterTags((prev) =>
                                      isSelected
                                        ? prev.filter((id) => id !== t.id)
                                        : [...prev, t.id],
                                    )
                                  }
                                  style={{
                                    fontSize: "var(--text-2xs)",
                                    background: isSelected ? color : "transparent",
                                    color: textColor,
                                    padding: "3px 8px",
                                    borderRadius: "6px",
                                    fontWeight: 900,
                                    cursor: "pointer",
                                    border: `1px solid ${color}`,
                                    boxShadow: isSelected ? `0 0 12px ${color}40` : "none",
                                    transition: "all 0.1s ease",
                                    textTransform: "uppercase",
                                    opacity: isSelected ? 1 : 0.8,
                                  }}
                                >
                                  {t.label}
                                </span>
                              );
                            })}
                          </div>
                        ))}
                    </div>
                  );
                })()}

                {/* Removed logger block to reposition it up */}

                {/* Participant Filter */}
                {participants && participants.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <div
                      style={{
                        fontSize: "var(--text-2xs)",
                        color: "#666",
                        fontWeight: "bold",
                        letterSpacing: "1px",
                      }}
                    >
                      {t("logger.data.participants")}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        overflowX: "auto",
                        paddingBottom: "4px",
                        scrollbarWidth: "none",
                        flexWrap: "wrap",
                      }}
                    >
                      {participants
                        .filter((p: any) => p.status !== "eliminated")
                        .sort((a: any, b: any) => (a.number || 0) - (b.number || 0))
                        .map((p: any) => {
                          const isSelected = selectedFilterParticipants.includes(p.id);
                          return (
                            <ParticipantBadge
                              key={p.id}
                              participant={p}
                              variant="pill"
                              size="sm"
                              isActive={isSelected}
                              onClick={() =>
                                setSelectedFilterParticipants((prev) =>
                                  isSelected ? prev.filter((id) => id !== p.id) : [...prev, p.id],
                                )
                              }
                            />
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div
        data-testid="data-column-content"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "0",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* HISTORY */}
        <div
          data-testid="data-column-history"
          style={{ flex: 1, overflowY: "auto", paddingBottom: "24px" }}
        >
          {rundownItems.map((rundown) => {
            // All stories for this event (Active & Completed)
            const eventStories = filteredStories.filter((s) => s.rundownId === rundown.id);

            if (eventStories.length === 0) return null;

            const isRundownExpanded = expandedRundownIds.includes(rundown.id);

            return (
              <div
                key={rundown.id}
                data-testid={`data-rundown-group-${rundown.id}`}
                style={{ borderBottom: "1px solid var(--color-panel-border)" }}
              >
                <div
                  data-testid={`data-rundown-header-${rundown.id}`}
                  onClick={() => toggleRundownExpansion(rundown.id)}
                  style={{
                    padding: "8px 15px",
                    background: rundown.status === "live" ? "rgba(59, 130, 246, 0.1)" : "#1a1a1a",
                    color:
                      rundown.status === "live"
                        ? "var(--color-data-event)"
                        : "var(--color-text-muted)",
                    fontSize: "var(--text-sm)",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span
                        style={{
                          marginRight: "8px",
                          display: "inline-flex",
                          width: "14px",
                          justifyContent: "center",
                        }}
                      >
                        <i
                          className={`bx ${isRundownExpanded ? "bx-caret-down" : "bx-caret-right"}`}
                          style={{ fontSize: "1.2em" }}
                        ></i>
                      </span>

                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveAiSummaryId(
                            activeAiSummaryId === rundown.id ? null : rundown.id,
                          );
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "14px",
                          padding: "0 4px",
                          borderRadius: "3px",
                          marginRight: "6px",
                          background: (rundown as any).summary
                            ? "#f72d60"
                            : "rgba(247, 45, 96, 0.15)",
                          color: (rundown as any).summary ? "#fff" : "#f72d60",
                          border: (rundown as any).summary ? "none" : "1px solid #f72d60",
                          fontSize: "var(--text-2xs)",
                          fontWeight: 800,
                          letterSpacing: "0.5px",
                          boxShadow: (rundown as any).summary ? "0 0 10px #f72d60" : "none",
                          userSelect: "none",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        Ai
                      </span>

                      <div
                        onMouseEnter={() => handleRundownHoverEnter(rundown.id)}
                        onMouseLeave={handleRundownHoverLeave}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "help",
                        }}
                      >
                        <i
                          className="bx bx-layer"
                          style={{
                            margin: "0 8px 0 0",
                            verticalAlign: "middle",
                            fontSize: "1.2em",
                            color:
                              hoveredRundownId === rundown.id
                                ? "var(--color-data-event)"
                                : "var(--color-data-rundown)",
                          }}
                        ></i>
                      </div>

                      {(() => {
                        const totalLogs = eventStories.reduce(
                          (acc, s) => acc + (s.logs?.length || 0),
                          0,
                        );
                        const starredStories = eventStories.filter(
                          (s) => (s.rating || 0) > 0,
                        ).length;
                        const starredLogs = eventStories.reduce(
                          (acc, s) =>
                            acc + (s.logs?.filter((l) => (l.rating || 0) > 0).length || 0),
                          0,
                        );
                        const hasStats = eventStories.length > 0 || totalLogs > 0;

                        return (
                          <TooltipProvider delay={200}>
                            <Tooltip>
                              <TooltipTrigger
                                render={<span style={{ cursor: "help" }}>{rundown.title}</span>}
                              />
                              <TooltipContent
                                side="top"
                                align="center"
                                className="bg-zinc-100 border-zinc-200 text-zinc-900 shadow-md px-3 py-2 z-[100]"
                              >
                                {hasStats ? (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      gap: "10px",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span
                                      title={t("logger.data.stories")}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <StoryIcon isActive={false} size={12} /> {eventStories.length}
                                    </span>
                                    <span
                                      title={t("logger.data.totalLogs")}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <LogIcon size={12} /> {totalLogs}
                                    </span>
                                    {(starredStories > 0 || starredLogs > 0) && (
                                      <span style={{ display: "inline-flex", gap: "8px" }}>
                                        {starredStories > 0 && (
                                          <span
                                            title={t("logger.data.highRatedStories")}
                                            style={{
                                              color: "var(--color-data-story)",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "4px",
                                            }}
                                          >
                                            ★ {starredStories}
                                          </span>
                                        )}
                                        {starredLogs > 0 && (
                                          <span
                                            title={t("logger.data.highRatedLogs")}
                                            style={{
                                              color: "var(--color-accent-warning)",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "4px",
                                            }}
                                          >
                                            ★ {starredLogs}
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      opacity: 0.6,
                                      fontSize: "0.8rem",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    No data
                                  </span>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                      {isSuperAdmin && (
                        <i
                          className="bx bx-trash"
                          title={t("logger.data.deleteEvent")}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick("event", rundown.id, rundown.title);
                          }}
                          style={{
                            marginLeft: "8px",
                            fontSize: "0.8rem",
                            color: "#444",
                            cursor: "pointer",
                            transition: "color 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "#ff4444";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "#444";
                          }}
                        />
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {rundown.status === "live" && (
                        <span
                          style={{
                            fontSize: "var(--text-2xs)",
                            background: "var(--color-accent-danger)",
                            color: "white",
                            padding: "1px 5px",
                            borderRadius: "4px",
                            marginLeft: "8px",
                          }}
                        >
                          {t("logger.data.live")}
                        </span>
                      )}
                      {rundown.isParallel && (
                        <span style={{ color: "var(--color-accent-primary)", marginLeft: "8px" }}>
                          ║
                        </span>
                      )}
                    </div>
                  </div>

                  {activeAiSummaryId === rundown.id && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        marginTop: "6px",
                        marginBottom: "6px",
                        padding: "12px 14px",
                        background:
                          "linear-gradient(180deg, rgba(30,30,30,1) 0%, rgba(20,20,20,1) 100%)",
                        borderRadius: "6px",
                        border: "1px solid var(--color-accent-primary)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                        animation: "fadeIn 0.2s ease-in-out",
                        cursor: "text",
                        overflow: "hidden",
                      }}
                    >
                      {/* Unified Stories Summary */}
                      <div>
                        <div
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--color-accent-primary)",
                            fontWeight: "900",
                            marginBottom: "6px",
                            letterSpacing: "0.5px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            textTransform: "uppercase",
                          }}
                        >
                          <i className="bx bx-brain" style={{ fontSize: "var(--text-md)" }}></i>{" "}
                          {t("logger.data.storiesSummary")}
                        </div>
                        {(rundown as any).summary ? (
                          <div
                            style={{
                              fontSize: "var(--text-sm)",
                              color: "#ddd",
                              lineHeight: "1.45",
                              fontWeight: "normal",
                              fontFamily: "var(--font-sans)",
                              textTransform: "none",
                              maxHeight: "150px",
                              overflowY: "auto",
                              scrollbarWidth: "thin" as any,
                              scrollbarColor: "rgba(247, 45, 96, 0.3) transparent",
                            }}
                          >
                            {formatSummary((rundown as any).summary)}
                          </div>
                        ) : (
                          <div
                            style={{
                              fontSize: "var(--text-sm)",
                              color: "#888",
                              fontStyle: "italic",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "4px 0",
                            }}
                          >
                            <i
                              className="bx bx-loader-alt bx-spin"
                              style={{ color: "var(--color-accent-primary)" }}
                            ></i>{" "}
                            {t("logger.data.analyzingStories")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {isRundownExpanded && (
                  <div style={{ background: "#111" }}>
                    {/* Sorting: Stories Oldest First (Chronological) */}
                    {eventStories
                      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
                      .map((story) => {
                        const isStoryExpanded = expandedStoryIds.includes(story.id);

                        return (
                          <div
                            key={story.id}
                            data-testid={`data-story-${story.id}`}
                            style={{
                              paddingLeft: "10px",
                              background:
                                draggedOverStoryId === story.id
                                  ? "rgba(247, 45, 96, 0.15)"
                                  : "transparent",
                              transition: "background 0.2s",
                            }}
                            onDragOver={(e) => {
                              if (e.dataTransfer.types.includes("application/json")) {
                                e.preventDefault();
                                setDraggedOverStoryId(story.id);
                              }
                            }}
                            onDragLeave={(e) => {
                              setDraggedOverStoryId(null);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              setDraggedOverStoryId(null);
                              try {
                                const data = JSON.parse(e.dataTransfer.getData("application/json"));
                                if (data.type === "SIDELOG" && onMoveSideLogToStory) {
                                  onMoveSideLogToStory(data.id, story.id);
                                }
                              } catch (err) {}
                            }}
                          >
                            <div
                              data-testid={`data-story-header-${story.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpansion(story.id);
                              }}
                              style={{
                                padding: "6px 15px 6px 15px",
                                color: "var(--color-data-story)",
                                fontSize: "var(--text-sm)",
                                fontWeight: "bold",
                                display: "flex",
                                flexDirection: "column", // MODIFIED: Stack vertically
                                cursor: "pointer",
                                borderBottom: "1px solid rgba(255,255,255,0.02)",
                                borderLeft: "4px solid transparent",
                                transition: "background 0.2s",
                                background:
                                  hoveredStoryId === story.id
                                    ? "rgba(255,255,255,0.03)"
                                    : "transparent",
                              }}
                            >
                              {/* HEADER ROW */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <span
                                    style={{
                                      opacity: 0.7,
                                      display: "inline-flex",
                                      width: "14px",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <i
                                      className={`bx ${isStoryExpanded ? "bx-caret-down" : "bx-caret-right"}`}
                                      style={{ fontSize: "0.9em" }}
                                    ></i>
                                  </span>

                                  <span
                                    data-testid={`data-story-ai-${story.id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveAiSummaryId(
                                        activeAiSummaryId === story.id ? null : story.id,
                                      );
                                    }}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      height: "14px",
                                      padding: "0 4px",
                                      borderRadius: "3px",
                                      marginRight: "4px",
                                      background: story.summary
                                        ? "#f72d60"
                                        : "rgba(247, 45, 96, 0.15)",
                                      color: story.summary ? "#fff" : "#f72d60",
                                      border: story.summary ? "none" : "1px solid #f72d60",
                                      fontSize: "var(--text-2xs)",
                                      fontWeight: 800,
                                      letterSpacing: "0.5px",
                                      boxShadow: story.summary ? "0 0 10px #f72d60" : "none",
                                      userSelect: "none",
                                      cursor: "pointer",
                                      transition: "all 0.2s ease",
                                    }}
                                  >
                                    {story.status === "complete" && !story.summary ? (
                                      <i
                                        className="bx bx-loader-alt bx-spin"
                                        style={{ marginRight: "2px", fontSize: "11px" }}
                                      ></i>
                                    ) : null}
                                    Ai
                                  </span>

                                  {/* HOVER TARGET: STORY ICON */}
                                  <div
                                    data-testid={`data-story-icon-${story.id}`}
                                    style={{
                                      position: "relative",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {/* Icon uses story color so it doesn't look gray and disabled */}
                                    <StoryIcon
                                      isActive={false} /* Story is not active/pulsing */
                                      color="var(--color-data-story)"
                                    />
                                  </div>

                                  <div
                                    style={{ display: "flex", alignItems: "center", gap: "6px" }}
                                  >
                                    {isSuperAdmin && (
                                      <input
                                        data-testid={`data-story-checkbox-${story.id}`}
                                        type="checkbox"
                                        checked={selectedStories.has(story.id)}
                                        onChange={() => {}}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleStorySelection(story.id, e as any);
                                        }}
                                        style={{
                                          marginRight: "6px",
                                          accentColor: "var(--color-accent-primary)",
                                          cursor: "pointer",
                                        }}
                                      />
                                    )}
                                    {(() => {
                                      const logCount = story.logs?.length || 0;
                                      const starredCount =
                                        story.logs?.filter((l) => (l.rating || 0) > 0).length || 0;
                                      const hasStats = logCount > 0 || starredCount > 0;

                                      return (
                                        <TooltipProvider delay={200}>
                                          <Tooltip>
                                            <TooltipTrigger
                                              render={
                                                <span
                                                  data-testid={`data-story-title-${story.id}`}
                                                  style={{ cursor: "help" }}
                                                >
                                                  {story.title}
                                                </span>
                                              }
                                            />
                                            <TooltipContent
                                              side="top"
                                              align="center"
                                              className="bg-zinc-100 border-zinc-200 text-zinc-900 shadow-md px-3 py-2 z-[100]"
                                            >
                                              {hasStats ? (
                                                <span
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                  }}
                                                >
                                                  <LogIcon size={11} /> {logCount}
                                                  {starredCount > 0 && (
                                                    <span
                                                      style={{
                                                        color: "var(--color-accent-warning)",
                                                        marginLeft: "4px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "2px",
                                                      }}
                                                    >
                                                      | ★ {starredCount}
                                                    </span>
                                                  )}
                                                </span>
                                              ) : (
                                                <span
                                                  style={{
                                                    opacity: 0.6,
                                                    fontSize: "0.8rem",
                                                    fontStyle: "italic",
                                                  }}
                                                >
                                                  No data
                                                </span>
                                              )}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      );
                                    })()}
                                    {(() => {
                                      const isOwner = !!user?.id && story.userId === user.id;
                                      if (!isSuperAdmin && !isOwner) return null;

                                      return (
                                        <i
                                          data-testid={`data-story-delete-${story.id}`}
                                          className="bx bx-trash"
                                          title={t("logger.data.deleteStory")}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (isSuperAdmin) {
                                              handleDeleteClick("story", story.id, story.title);
                                            } else {
                                              setOwnerDeleteStory({
                                                id: story.id,
                                                title: story.title,
                                              });
                                            }
                                          }}
                                          style={{
                                            marginLeft: "6px",
                                            fontSize: "0.75rem",
                                            color: "#444",
                                            cursor: "pointer",
                                            transition: "color 0.15s",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.color = "#ff4444";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.color = "#444";
                                          }}
                                        />
                                      );
                                    })()}
                                    {onEditStory && (
                                      <span
                                        data-testid={`data-story-edit-${story.id}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onEditStory(story);
                                        }}
                                        title={t("logger.data.editStory")}
                                        style={{
                                          opacity: 0.6,
                                          cursor: "pointer",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          marginLeft: "6px",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.color = "#fff";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.color = "inherit";
                                        }}
                                      >
                                        <i
                                          className="bx bx-pencil"
                                          style={{ fontSize: "0.9em" }}
                                        ></i>
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Story Rating (Pink Stars) */}
                                <div
                                  data-testid={`data-story-rating-${story.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <StarRating
                                    value={story.rating || 0}
                                    onChange={(v) => {
                                      if (onUpdateStory) {
                                        onUpdateStory({
                                          ...story,
                                          rating: v > 0 ? v : 0,
                                        });
                                      }
                                    }}
                                    activeColor="var(--color-data-story)"
                                    size="var(--text-sm)"
                                    gap="1px"
                                    starTestIdPrefix={`data-story-star-${story.id}`}
                                  />
                                </div>
                              </div>

                              {/* INLINE SUMMARY (Triggered by Click on Ai badge) */}
                              {activeAiSummaryId === story.id && (
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    marginTop: "6px",
                                    marginBottom: "6px",
                                    padding: "12px 14px",
                                    background:
                                      "linear-gradient(180deg, rgba(30,30,30,1) 0%, rgba(20,20,20,1) 100%)",
                                    borderRadius: "6px",
                                    border: "1px solid var(--color-accent-primary)",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                                    animation: "fadeIn 0.2s ease-in-out",
                                    cursor: "text",
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    scrollbarWidth: "thin" as any,
                                    scrollbarColor: "rgba(247, 45, 96, 0.3) transparent",
                                  }}
                                >
                                  {story.summary || (story as any).aiSummary ? (
                                    <>
                                      <div
                                        style={{
                                          fontSize: "var(--text-sm)",
                                          color: "var(--color-accent-primary)",
                                          fontWeight: "900",
                                          marginBottom: "6px",
                                          letterSpacing: "0.5px",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "6px",
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        <i
                                          className="bx bx-brain"
                                          style={{ fontSize: "var(--text-md)" }}
                                        ></i>{" "}
                                        SUMMARY
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "var(--text-sm)",
                                          color: "#ddd",
                                          lineHeight: "1.45",
                                          fontWeight: "normal",
                                          fontFamily: "var(--font-sans)",
                                        }}
                                      >
                                        {formatSummary(story.summary || (story as any).aiSummary)}
                                      </div>
                                    </>
                                  ) : (
                                    <div
                                      style={{
                                        fontSize: "var(--text-sm)",
                                        color: "#888",
                                        fontStyle: "italic",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "4px 0",
                                      }}
                                    >
                                      {story.status === "complete" ? (
                                        <>
                                          <i
                                            className="bx bx-loader-alt bx-spin"
                                            style={{ color: "var(--color-accent-primary)" }}
                                          ></i>{" "}
                                          Generando resumen en proceso...
                                        </>
                                      ) : (
                                        <>
                                          <i
                                            className="bx bx-info-circle"
                                            style={{ color: "#888" }}
                                          ></i>{" "}
                                          Aún no tiene resumen
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {isStoryExpanded && (
                              <div
                                style={{
                                  padding: "8px 12px",
                                  background: "rgba(0,0,0,0.15)",
                                  borderTop: "1px solid rgba(255,255,255,0.05)",
                                  borderBottom: "1px solid rgba(255,255,255,0.05)",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "8px",
                                }}
                              >
                                {/* Mostrar tags actuales y boton editar */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "4px",
                                      flexWrap: "wrap",
                                      alignItems: "center",
                                    }}
                                  >
                                    <i
                                      className="bx bx-purchase-tag"
                                      style={{
                                        color: "#888",
                                        fontSize: "var(--text-base)",
                                        marginRight: "4px",
                                      }}
                                    ></i>
                                    <span
                                      style={{
                                        fontSize: "var(--text-xs)",
                                        color: "#666",
                                        fontWeight: "bold",
                                        marginRight: "4px",
                                      }}
                                    >
                                      METADATA:
                                    </span>
                                    {!story.tags?.length &&
                                      !story.cameras?.length &&
                                      !story.participants?.length && (
                                        <span
                                          style={{
                                            fontSize: "var(--text-sm)",
                                            color: "#555",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          None
                                        </span>
                                      )}
                                    {story.cameras?.map((c) => {
                                      const camName = getCameraName(c);
                                      return camName ? (
                                        <Badge
                                          key={`sc-${c}`}
                                          variant="outline"
                                          size="xs"
                                          className="border-0 bg-[#ff9933] text-black rounded-full"
                                        >
                                          {camName}
                                        </Badge>
                                      ) : null;
                                    })}
                                    {story.participants?.map((pId) => {
                                      const pObj = participants.find(
                                        (p) => p.id.toLowerCase() === pId.toLowerCase(),
                                      );
                                      return pObj ? (
                                        <ParticipantBadge
                                          key={`sp-${pId}`}
                                          participant={pObj}
                                          variant="pill"
                                          size="sm"
                                        />
                                      ) : null;
                                    })}
                                    {story.tags?.map((t) => {
                                      const c = getTagColor(t);
                                      return (
                                        <Badge
                                          key={`st-${t}`}
                                          variant="outline"
                                          size="xs"
                                          className="border-0 uppercase"
                                          style={{
                                            background: c,
                                            color: isColorLight(c) ? "#000" : "#fff",
                                          }}
                                        >
                                          {getTagLabel(t)}
                                        </Badge>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            )}

                            {isStoryExpanded &&
                              (() => {
                                const hasActiveFilters =
                                  minStars > 0 ||
                                  minLogStars > 0 ||
                                  selectedFilterTags.length > 0 ||
                                  selectedFilterParticipants.length > 0 ||
                                  selectedFilterCameras.length > 0 ||
                                  selectedFilterAuthors.length > 0 ||
                                  searchQuery.trim() !== "";

                                // Determine which logs to show
                                let logsToDisplay = story.logs || [];
                                if (hasActiveFilters) {
                                  // Show only filtered logs
                                  logsToDisplay = logsToDisplay.filter((log) => {
                                    // 1. Log Star Rating Filter
                                    if (minLogStars === 1) {
                                      if ((log.rating || 0) !== 1) return false;
                                    } else if (minLogStars === 2) {
                                      if ((log.rating || 0) < 2) return false;
                                    }

                                    // 3. Specific Tag Filter
                                    if (selectedFilterTags.length > 0) {
                                      const hasMatchingTag = selectedFilterTags.every((filterTag) =>
                                        (log.tags || []).includes(filterTag),
                                      );
                                      if (!hasMatchingTag) return false;
                                    }

                                    // 4. Participant Filter
                                    if (selectedFilterParticipants.length > 0) {
                                      const hasMatchingParticipant =
                                        selectedFilterParticipants.some((participantId) =>
                                          (log.participants || []).includes(participantId),
                                        );
                                      if (!hasMatchingParticipant) return false;
                                    }

                                    // 4.5. Author Filter
                                    if (selectedFilterAuthors.length > 0) {
                                      if (!selectedFilterAuthors.includes(log.author || ""))
                                        return false;
                                    }

                                    // 4.6. Camera Filter
                                    if (selectedFilterCameras.length > 0) {
                                      const hasMatchingCamera = selectedFilterCameras.every(
                                        (filterCam) => (log.cameras || []).includes(filterCam),
                                      );
                                      if (!hasMatchingCamera) return false;
                                    }

                                    // 4.7. Director Filter
                                    if (selectedFilterDirectors.length > 0) {
                                      if (
                                        !log.directorId ||
                                        !selectedFilterDirectors.includes(log.directorId)
                                      ) {
                                        return false;
                                      }
                                    }

                                    // 5. Search Query
                                    if (searchQuery.trim()) {
                                      const query = searchQuery.toLowerCase();
                                      const logText = (log.text || "").toLowerCase();
                                      const author = (story.author || "").toLowerCase();

                                      const hasParticipantMatch = log.participants?.some((pId) => {
                                        const p = participants.find((part: any) => part.id === pId);
                                        return (
                                          p &&
                                          ((p.name || "").toLowerCase().includes(query) ||
                                            (p.alias || "").toLowerCase().includes(query) ||
                                            p.number?.toString() === query)
                                        );
                                      });

                                      if (
                                        !logText.includes(query) &&
                                        !author.includes(query) &&
                                        !hasParticipantMatch
                                      ) {
                                        return false;
                                      }
                                    }

                                    return true;
                                  });
                                }

                                return (
                                  <div>
                                    {/* Sorting: Logs Oldest First (Chronological) */}
                                    {logsToDisplay.length === 0 ? (
                                      <div
                                        style={{
                                          padding: "16px",
                                          textAlign: "center",
                                          color: "#888",
                                          fontSize: "var(--text-sm)",
                                          fontStyle: "italic",
                                          background: "rgba(0,0,0,0.2)",
                                          borderRadius: "6px",
                                          marginTop: "8px",
                                        }}
                                      >
                                        {!story.logs || story.logs.length === 0
                                          ? t(
                                              "logger.data.noLogsInStory",
                                              "Esta historia no tiene logs.",
                                            )
                                          : t(
                                              "logger.data.noLogsMatch",
                                              "No hay logs que coincidan con los filtros.",
                                            )}
                                      </div>
                                    ) : (
                                      [...logsToDisplay]
                                        .sort((a, b) => a.tc.localeCompare(b.tc))
                                        .map((log) => (
                                          <LogCard
                                            key={log.id}
                                            compact
                                            showInlineRating
                                            log={{
                                              ...log,
                                              storyTags: story.tags,
                                              storyParticipants: story.participants,
                                              storyRating: story.rating,
                                              streams: story.cameras,
                                              storyDirectorId: story.directorId,
                                              storyName: story.title,
                                            }}
                                            participants={participants}
                                            directors={directors}
                                            accentColor="#F72D60"
                                            getCameraName={getCameraName}
                                            getTagColor={getTagColor}
                                            getTagLabel={getTagLabel}
                                            onUpdateItem={
                                              onUpdateStory
                                                ? (logId: string, updates: Partial<LogEntry>) => {
                                                    onUpdateStory({
                                                      ...story,
                                                      logs: (story.logs || []).map((storyLog) =>
                                                        storyLog.id === logId
                                                          ? { ...storyLog, ...updates }
                                                          : storyLog,
                                                      ),
                                                    });
                                                  }
                                                : undefined
                                            }
                                            onSaveText={
                                              onUpdateStory
                                                ? (newText: string) => {
                                                    const updatedStory = {
                                                      ...story,
                                                      logs: (story.logs || []).map((l) =>
                                                        l.id === log.id
                                                          ? { ...l, text: newText }
                                                          : l,
                                                      ),
                                                    };
                                                    onUpdateStory(updatedStory);
                                                  }
                                                : undefined
                                            }
                                          />
                                        ))
                                    )}
                                  </div>
                                );
                              })()}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Side Logs (Sweeper) */}
      {!hideSweeper && (
        <div
          style={{
            minHeight: selectedLogIds.length > 0 ? "280px" : "150px",
            maxHeight: "350px",
            borderTop: "1px solid var(--color-panel-border)",
            background: "var(--color-bg-mesh)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "8px var(--spacing-md)",
              fontSize: "var(--text-sm)",
              color: "var(--color-text-muted)",
              borderBottom: "1px solid var(--color-panel-border)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{t("logger.data.sideLogSweeper")}</span>
            {sideLogs.length > 0 && (
              <span style={{ fontSize: "var(--text-sm)", opacity: 0.6 }}>
                {t("logger.data.clickToSelect")}
              </span>
            )}
          </div>

          {selectedLogIds.length > 0 && (
            <div
              style={{
                padding: "8px 12px",
                margin: "8px",
                background: "rgba(247, 45, 96, 0.2)",
                border: "1px solid var(--color-accent-danger)",
                borderRadius: "6px",
                textAlign: "center",
                color: "var(--color-accent-danger)",
                fontSize: "var(--text-sm)",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                📌 {selectedLogIds.length} {t("logger.data.logsSelected")}
              </span>
              <button
                onClick={() => setSelectedLogIds([])}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {t("logger.data.cancel")}
              </button>
            </div>
          )}

          {/* Create New Story Interface when selection active */}
          {selectedLogIds.length > 0 && (
            <div style={{ padding: "0 8px 8px 8px" }}>
              {!showNewStoryInput ? (
                <Button
                  size="sm"
                  className="w-full bg-[var(--color-accent-danger)] text-white/80 hover:bg-[var(--color-accent-danger)]/90"
                  onClick={() => setShowNewStoryInput(true)}
                >
                  {t("logger.data.createNewStory", "+ CREATE NEW STORY")}
                </Button>
              ) : (
                <div className="flex gap-1.5">
                  <Input
                    autoFocus
                    type="text"
                    value={newStoryName}
                    onChange={(e) => setNewStoryName(e.target.value)}
                    placeholder={t("logger.data.newStoryPlaceholder", "New Story Name...")}
                    className="h-8 flex-1 bg-[#222]"
                    onKeyDown={(e) => e.key === "Enter" && handleCreateNewStory()}
                  />
                  <Button
                    size="sm"
                    className="bg-[var(--color-accent-danger)] text-white/80 hover:bg-[var(--color-accent-danger)]/90"
                    onClick={handleCreateNewStory}
                  >
                    {t("logger.data.ok", "OK")}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div
            data-testid="data-column-sweeper-list"
            style={{ flex: 1, overflowY: "auto", padding: "0 8px 30px 8px" }}
          >
            {sideLogs
              .slice()
              .reverse()
              .map((log) => {
                const isTranscription = (log as any).type === "transcription";
                const isOtf = (log as any).type === "otf";

                let _bgColor = selectedLogIds.includes(log.id)
                  ? "rgba(247, 45, 96, 0.25)"
                  : "rgba(247, 45, 96, 0.12)";
                let baseBorderColor = "#f72d60";
                let baseBgColor = selectedLogIds.includes(log.id)
                  ? "rgba(247, 45, 96, 0.25)"
                  : "rgba(247, 45, 96, 0.12)";

                let tcColor = "rgba(255, 255, 255, 0.8)";
                let textColor = "#eee";
                let opacity = 1;
                let textStyle = "normal";

                if (!selectedLogIds.includes(log.id)) {
                  if (isOtf) {
                    baseBgColor = "rgba(0, 229, 255, 0.12)";
                    baseBorderColor = "#00e5ff";
                    tcColor = "#00e5ff";
                  } else if (isTranscription) {
                    baseBgColor = "rgba(156, 39, 176, 0.12)";
                    baseBorderColor = "#9c27b0";
                    tcColor = "#9c27b0";
                    opacity = 0.8;
                    textColor = "#ccc";
                    textStyle = "italic";
                  }
                }

                let finalBorderLeft = `3px solid ${baseBorderColor}`;
                let finalBg = baseBgColor;

                return (
                  <div
                    key={log.id}
                    data-testid={`data-sidelog-${log.id}`}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(
                        "application/json",
                        JSON.stringify({ type: "SIDELOG", id: log.id }),
                      );
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    style={{
                      padding:
                        log.rating && log.rating >= 4 && !selectedLogIds.includes(log.id)
                          ? "8px 10px 8px 13px"
                          : "8px 10px",
                      borderBottom: "1px solid #333",
                      fontSize: "var(--text-sm)",
                      background: selectedLogIds.includes(log.id) ? baseBgColor : finalBg,
                      border: selectedLogIds.includes(log.id)
                        ? "1px solid var(--color-accent-danger)"
                        : "1px solid transparent",
                      borderLeft: selectedLogIds.includes(log.id)
                        ? "3px solid var(--color-accent-danger)"
                        : finalBorderLeft,
                      borderRadius: "6px",
                      marginTop: "4px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      cursor: "pointer",
                      opacity: opacity,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectLog(log.id);
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ display: "flex", gap: "8px", flex: 1 }}>
                        {/* TC */}
                        <span
                          style={{
                            color: tcColor,
                            fontFamily: "var(--font-mono)",
                            fontSize: "var(--text-sm)",
                            fontWeight: "bold",
                            minWidth: "60px",
                          }}
                        >
                          {log.tc}
                        </span>

                        {/* Text */}
                        <span
                          style={{
                            color: textColor,
                            wordBreak: "break-word",
                            lineHeight: "1.2",
                            flex: 1,
                            minHeight: "1.2em",
                            fontStyle: textStyle as any,
                          }}
                        >
                          {log.text ? (
                            formatLogText(log.text, participants)
                          ) : (
                            <span style={{ fontStyle: "italic", opacity: 0.5 }}>
                              {t("logger.data.noTextContent")}
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                        {log.rating && log.rating > 0 && (
                          <StarRating
                            value={log.rating}
                            max={2}
                            readOnly
                            size="var(--text-sm)"
                            activeColor="var(--color-accent-warning)"
                          />
                        )}
                      </div>
                    </div>

                    {/* Metadata Pills */}
                    {log.cameras?.length ||
                    log.participants?.length ||
                    log.tags?.length ||
                    log.directorId ? (
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          flexWrap: "wrap",
                          paddingLeft: "58px",
                        }}
                      >
                        {log.cameras?.map((c) => {
                          const camName = getCameraName(c);
                          return camName ? (
                            <span
                              key={c}
                              style={{
                                fontSize: "var(--text-2xs)",
                                background: "#ffaa00",
                                color: "#000",
                                padding: "0 6px",
                                height: "15px",
                                borderRadius: "20px",
                                fontWeight: "700",
                                textTransform: "uppercase",
                                letterSpacing: "0.2px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                lineHeight: "1",
                              }}
                            >
                              {camName}
                            </span>
                          ) : null;
                        })}
                        {log.participants?.map((p) => {
                          const partObj = participants.find((part: any) => part.id === p);
                          const label = partObj?.number
                            ? `CH${partObj.number} ${partObj.alias || partObj.name}`
                            : partObj?.alias || partObj?.name || p;
                          const pColor = getParticipantColor(
                            partObj?.metadata?.colorId,
                            partObj?.metadata?.customColor,
                          );
                          return (
                            <span
                              key={p}
                              style={{
                                fontSize: "var(--text-2xs)",
                                background: pColor.bg,
                                color: pColor.text,
                                padding: "0 8px",
                                height: "18px",
                                borderRadius: "6px",
                                fontWeight: "900",
                                textTransform: "uppercase",
                                letterSpacing: "0.2px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                lineHeight: "1",
                                border: `1px solid ${pColor.border}`,
                              }}
                            >
                              {label}
                            </span>
                          );
                        })}
                        {log.tags?.map((t) => {
                          const color = getTagColor(t);
                          return (
                            <span
                              key={t}
                              style={{
                                fontSize: "var(--text-2xs)",
                                background: color,
                                color: isColorLight(color) ? "#000" : "#fff",
                                padding: "0 8px",
                                height: "18px",
                                borderRadius: "6px",
                                fontWeight: "900",
                                textTransform: "uppercase",
                                letterSpacing: "0.2px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                lineHeight: "1",
                                border: `1px solid ${color}`,
                              }}
                            >
                              {getTagLabel(t)}
                            </span>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            {sideLogs.length === 0 && (
              <div
                style={{
                  padding: "20px",
                  textAlign: "center",
                  color: "#666",
                  fontSize: "var(--text-sm)",
                  fontStyle: "italic",
                }}
              >
                {t("logger.data.noLogsYet")}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating bulk delete bar */}
      {isSuperAdmin && selectedStories.size > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "linear-gradient(135deg, #1e1e1e 0%, #141414 100%)",
            border: "1px solid rgba(255,60,60,0.3)",
            borderRadius: "12px",
            padding: "10px 20px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
            zIndex: 9999,
          }}
        >
          <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>
            {selectedStories.size} {t("logger.data.selected")}
          </span>
          <button
            onClick={() => {
              setSelectedStories(new Set());
            }}
            style={{
              padding: "6px 14px",
              borderRadius: "6px",
              border: "1px solid #444",
              background: "transparent",
              color: "#ccc",
              cursor: "pointer",
              fontSize: "0.8rem",
            }}
          >
            {t("logger.data.deselect")}
          </button>
          <button
            onClick={() => setBulkConfirmOpen(true)}
            style={{
              padding: "6px 14px",
              borderRadius: "6px",
              border: "none",
              background: "linear-gradient(135deg, #cc3333 0%, #aa2222 100%)",
              color: "#fff",
              cursor: "pointer",
              fontSize: "0.8rem",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <i className="bx bx-trash" /> {t("logger.data.delete")}
          </button>
        </div>
      )}

      {/* Bulk delete confirmation modal */}
      <Dialog
        open={bulkConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !bulkDeleting) setBulkConfirmOpen(false);
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="sm:max-w-[440px] border-red-500/30 bg-gradient-to-b from-[#1e1e1e] to-[#141414] shadow-[0_20px_60px_rgba(0,0,0,0.6),0_0_30px_rgba(255,60,60,0.1)]"
        >
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-[10px] bg-red-500/15 flex items-center justify-center">
                <i className="bx bx-trash text-lg text-[#ff4444]" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-white">
                  {t("logger.data.bulkDelete")}
                </DialogTitle>
                <DialogDescription className="text-sm text-[#aaa]">
                  {selectedStories.size} {t("logger.data.storiesSelected")}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="bg-red-500/[0.06] rounded-lg p-3.5 border border-red-500/[0.12]">
            <div className="text-sm text-[#ff6666] font-bold mb-2.5 uppercase tracking-wide">
              {t("logger.data.willBeDeleted")}
            </div>
            <div className="flex items-center gap-2 text-base">
              <i className="bx bx-file text-[#ff6666]" />
              <span className="text-[#ddd]">{selectedStories.size}</span>
              <span className="text-[#888]">{t("logger.data.stories")}</span>
            </div>
          </div>
          <div className="text-sm text-[#999] leading-relaxed italic">
            {t("logger.data.cannotUndo")}
          </div>
          <DialogFooter className="bg-transparent border-t-0 mx-0 mb-0 p-0 flex-row justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkConfirmOpen(false)}
              disabled={bulkDeleting}
            >
              {t("logger.data.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={bulkDeleting}
              onClick={async () => {
                const ids = Array.from(selectedStories);
                if (ids.length === 0) return;
                setBulkDeleting(true);
                try {
                  const items = ids.map((id) => {
                    const story = stories.find((s) => s.id === id);
                    return { type: "story", id, name: story?.title || id };
                  });

                  const res = await fetch(`${config.API_URL}/api/admin/bulk-delete`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ items }),
                  });

                  if (!res.ok) throw new Error("Server error: " + res.status);

                  selectedStories.clear();

                  window.location.reload();
                } catch (err: any) {
                  console.error("Delete failed:", err);
                  setGlobalAlert({
                    isOpen: true,
                    title: t("logger.data.error"),
                    message: t("logger.data.deleteFailed") + err.message,
                  });
                  setBulkDeleting(false);
                }
              }}
            >
              {bulkDeleting ? (
                <>
                  <i className="bx bx-loader-alt bx-spin" /> {t("logger.data.deleting")}
                </>
              ) : (
                <>
                  <i className="bx bx-trash" /> {t("logger.data.deleteConfirm")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isSuperAdmin && (
        <DeleteCascadeModal
          isOpen={deleteModal.isOpen}
          onClose={() => setDeleteModal((p) => ({ ...p, isOpen: false }))}
          onConfirm={handleDeleteConfirm}
          type={deleteModal.type}
          name={deleteModal.name}
          counts={deleteModal.counts}
          isLoading={deleteModal.isLoading}
          isDeleting={deleteModal.isDeleting}
        />
      )}

      <CustomDialog
        isOpen={!!globalAlert?.isOpen}
        type="alert"
        title={globalAlert?.title || t("logger.data.attention")}
        message={globalAlert?.message || ""}
        onConfirm={() => setGlobalAlert(null)}
        onCancel={() => setGlobalAlert(null)}
      />

      <CustomDialog
        isOpen={!!ownerDeleteStory}
        type="confirm"
        title={t("logger.data.deleteStory", "Eliminar historia")}
        message={t(
          "logger.data.deleteOwnStoryConfirm",
          `¿Eliminar "${ownerDeleteStory?.title || ""}"? Esta acción también borrará todos sus logs y no se puede deshacer.`,
        )}
        confirmText={
          ownerDeleting
            ? t("logger.data.deleting", "Eliminando...")
            : t("logger.data.deleteConfirm", "Eliminar")
        }
        onConfirm={handleOwnerDeleteConfirm}
        onCancel={() => (ownerDeleting ? null : setOwnerDeleteStory(null))}
      />
    </div>
  );
};
